import { useState } from "react";
import AccordionGraphics from "../../accordion-graphics";
import MonthlyAverage from "../../../pages/planning/components/sections/monthly-average";
import VariableIndirectCosts from "../../../pages/planning/components/sections/variable-indirect-costs";
import ApiLab from "../../../services/lab";
import Button from "../../button/button";
import TotalInvestment from "../../../pages/planning/components/sections/total-investment";
import PostCreditSection from "../../../pages/post-credit/results/postCreditSection";
import {eventTag, sortedArrayByObjectKeys} from "../../../services/utils";
import PostCreditCashFlow from "../../../pages/post-credit/results/postCreditCashFlow";
import { formatCurrency } from "../../../services/utils";
import LabVariations from "./labVariations";
import moment from "moment";

const apiLab = ApiLab();

const parseMoneyString = (value) => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^\d,.-]/g, '').replace(',', '.');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

const parseCurrencyToFloat = (value) => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    return parseFloat(
      value
        .replace("R$", "")
        .trim()
        .replace(/\./g, "")
        .replace(",", ".")
    );
  }
  return 0;
};

const EditLabPlanning = ({
  planToEditData,
  planningId,
  labId,
  onUpdateSuccess,
  clickedMonth,
  planningProfit,
  eventLabGA,
}) => {
  const [openedSections, setOpenedSections] = useState(["variations"]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [invalidForm, setInvalidForm] = useState({
    receivement: false,
    payment: false,
    inventory: false,
  });
  const [variationData, setVariationData] = useState({
    VGdQV: planToEditData.VGdQV,
    VGdPV: planToEditData.VGdPV,
  });
  const [monthAverageData, setMonthAverageData] = useState({
    cif: planToEditData.cif,
    cmp: planToEditData.cmp,
    cms: planToEditData.cms,
    rtp: planToEditData.rtp,
    rts: planToEditData.rts,
  });
  const [variableIndirectCostsData, setVariableIndirectCostsData] = useState({
    cc: { pdt: formatCurrency(planToEditData.cc.pdt), pdrt: formatCurrency(planToEditData.cc.pdrt) },
    ci: { pdt: formatCurrency(planToEditData.ci.pdt), pdrt: formatCurrency(planToEditData.ci.pdrt) },
    ou: { pdt: formatCurrency(planToEditData.ou.pdt), pdrt: formatCurrency(planToEditData.ou.pdrt) },
    tcc: { pdt: formatCurrency(planToEditData.tcc.pdt), pdrt: formatCurrency(planToEditData.tcc.pdrt) },
    tcd: { pdt: formatCurrency(planToEditData.tcd.pdt), pdrt: formatCurrency(planToEditData.tcd.pdrt) },
  });
  const [totalInvestmentData, setTotalInvestmentData] = useState({
    cgp: planToEditData.cgp,
    iif: planToEditData.iif,
  });
  const [receivementDeadlinesData, setReceivementDeadlinesData] = useState(
    planToEditData?.pdr?.length ? sortedArrayByObjectKeys(planToEditData?.pdr) : []
  );
  const [paymentDeadlinesData, setPaymentDeadlinesData] = useState(
    planToEditData?.pdc?.length ? sortedArrayByObjectKeys(planToEditData?.pdc) : []
  );
  const [inventoryTurnoverData, setInventoryTurnoverData] = useState(
    planToEditData?.gde?.length ? sortedArrayByObjectKeys(planToEditData?.gde) : [{ order: 0, days: "", value: 0 }]
  );
  const [paymentCapacityData, setPaymentCapacityData] = useState(
    planToEditData?.fluxo?.saidas.length ? [planToEditData?.fluxo?.saidas.find(item => item.mes === clickedMonth)] : []
  );

  const handleAccordionToggle = (section) => {
    setOpenedSections(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const handleInvalidFormChange = (step) => (isInvalid) => {
    setInvalidForm(prev => ({
      ...prev,
      [step]: isInvalid
    }));
  };

  const handleObjectChange = (stepSetter) => (key, value) => {
    stepSetter((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleNestedObjectChange = (stepSetter) => (key, value) => {
    const [parent, child] = key.split(".");
    stepSetter((prev) => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [child]: value,
      },
    }));
  };

  const handleArrayChange = (stepSetter) => (data) => {
    stepSetter(data);
  };

  const handleUpdateAll = async () => {
    setIsUpdating(true);

    const payload = {
      VGdQV: variationData.VGdQV,
      VGdPV: variationData.VGdPV,

      cif: parseCurrencyToFloat(monthAverageData.cif),
      cmp: parseCurrencyToFloat(monthAverageData.cmp),
      cms: parseCurrencyToFloat(monthAverageData.cms),
      rtp: parseCurrencyToFloat(monthAverageData.rtp),
      rts: parseCurrencyToFloat(monthAverageData.rts),

      cc: {
        pdt: parseMoneyString(variableIndirectCostsData.cc.pdt),
        pdrt: parseMoneyString(variableIndirectCostsData.cc.pdrt),
      },
      ci: {
        pdt: parseMoneyString(variableIndirectCostsData.ci.pdt),
        pdrt: parseMoneyString(variableIndirectCostsData.ci.pdrt),
      },
      ou: {
        pdt: parseMoneyString(variableIndirectCostsData.ou.pdt),
        pdrt: parseMoneyString(variableIndirectCostsData.ou.pdrt),
      },
      tcc: {
        pdt: parseMoneyString(variableIndirectCostsData.tcc.pdt),
        pdrt: parseMoneyString(variableIndirectCostsData.tcc.pdrt),
      },
      tcd: {
        pdt: parseMoneyString(variableIndirectCostsData.tcd.pdt),
        pdrt: parseMoneyString(variableIndirectCostsData.tcd.pdrt),
      },

      cgp: parseCurrencyToFloat(totalInvestmentData.cgp),
      iif: parseCurrencyToFloat(totalInvestmentData.iif),

      pdr: receivementDeadlinesData.map(item => ({
        order: item.order,
        value: parseMoneyString(item.value),
      })),

      pdc: paymentDeadlinesData.map(item => ({
        order: item.order,
        value: parseMoneyString(item.value),
      })),

      gde: inventoryTurnoverData.map(item => ({
        order: item.order,
        days: Number(item.days),
        value: parseMoneyString(item.value),
      })),
    };

    apiLab.updateLabPlan(planningId, labId, payload)
      .then((res) => {
        if (onUpdateSuccess) {
          onUpdateSuccess(res.data);
        }
        eventTag(eventLabGA?.name, eventLabGA?.category, "submit", `Atualizando dados de comparação`)
      })
      .catch((err) => {
        console.log(err)
      })
      .finally(() => {
        setIsUpdating(false);
      })
  };

  const handleVariationsBlur = (fieldName, finalValue) => {
    setVariationData((prev) => ({
      ...prev,
      [fieldName]: finalValue,
    }));
  }

  const errorMessages = [
    invalidForm.receivement && 'Prazo de recebimento',
    invalidForm.payment && 'Prazo de pagamento',
    invalidForm.inventory && 'Giro de estoque',
  ].filter(Boolean);

  const renderError = () => {
    if (!errorMessages.length) return null;
    const plural = errorMessages.length > 1;
    const last = errorMessages[errorMessages.length - 1];
    const rest = errorMessages.slice(0, -1);
    const subject = plural ? `${rest.join(', ')} e ${last}` : last;
    const predicate = plural ? 'devem ser iguais a 100%' : 'deve ser igual a 100%';
    return `${subject} ${predicate}`;
  };

  return (
    <div className="lab-modal-edit-container">
      <div className="variations-accordion-container">
        <AccordionGraphics
          title="Variações"
          isOpen={openedSections.includes("variations")}
          onToggle={() => {
            handleAccordionToggle("variations")
            eventTag(eventLabGA?.name, eventLabGA?.category, eventLabGA?.action, `Accordion: Variações`)
          }}
        >
          <LabVariations
            quantityVariationInitial={variationData.VGdQV}
            priceVariationInitial={variationData.VGdPV}
            onVariationBlur={handleVariationsBlur}
          />
        </AccordionGraphics>
      </div>

      <AccordionGraphics
        title="Média Mensal"
        isOpen={openedSections.includes("monthly-average")}
        onToggle={() => {
          handleAccordionToggle("monthly-average")
          eventTag(eventLabGA?.name, eventLabGA?.category, eventLabGA?.action, `Accordion: Média Mensal`)
        }}
      >
        <MonthlyAverage
          data={monthAverageData}
          isDisabled={isUpdating}
          onChange={handleObjectChange(setMonthAverageData)}
          lab
        />
      </AccordionGraphics>

      <AccordionGraphics
        title="Custos Indiretos Variáveis"
        isOpen={openedSections.includes("variable-indirect-costs")}
        onToggle={() => {
          handleAccordionToggle("variable-indirect-costs")
          eventTag(eventLabGA?.name, eventLabGA?.category, eventLabGA?.action, `Accordion: Custos Indiretos Variáveis`)
        }}
      >
        <VariableIndirectCosts
          data={variableIndirectCostsData}
          isDisabled={isUpdating}
          onChange={handleNestedObjectChange(setVariableIndirectCostsData)}
          lab
        />
      </AccordionGraphics>

      <AccordionGraphics
        title="Investimento Total"
        isOpen={openedSections.includes("total-investments")}
        onToggle={() => {
          handleAccordionToggle("total-investments")
          eventTag(eventLabGA?.name, eventLabGA?.category, eventLabGA?.action, `Accordion: Investimento Total`)
        }}
      >
        <TotalInvestment
          data={totalInvestmentData}
          isDisabled={isUpdating}
          onChange={handleObjectChange(setTotalInvestmentData)}
          lab
        />
      </AccordionGraphics>

      <AccordionGraphics
        title="Prazos de Recebimento"
        isOpen={openedSections.includes("receivement-deadlines")}
        onToggle={() => {
          handleAccordionToggle("receivement-deadlines")
          eventTag(eventLabGA?.name, eventLabGA?.category, eventLabGA?.action, `Accordion: Prazos de Recebimento`)
        }}
      >
        <PostCreditSection
          step={5}
          data={receivementDeadlinesData}
          onChange={handleArrayChange(setReceivementDeadlinesData)}
          isDisabled={isUpdating}
          onInvalidChange={handleInvalidFormChange('receivement')}
          lab
        />
      </AccordionGraphics>

      <AccordionGraphics
        title="Prazos de Pagamento"
        isOpen={openedSections.includes("payment-deadlines")}
        onToggle={() => {
          handleAccordionToggle("payment-deadlines")
          eventTag(eventLabGA?.name, eventLabGA?.category, eventLabGA?.action, `Accordion: Prazos de Pagamento`)
        }}
      >
        <PostCreditSection
          step={6}
          data={paymentDeadlinesData}
          onChange={handleArrayChange(setPaymentDeadlinesData)}
          isDisabled={isUpdating}
          onInvalidChange={handleInvalidFormChange('payment')}
          lab
        />
      </AccordionGraphics>

      <AccordionGraphics
        title="Giro de Estoque"
        isOpen={openedSections.includes("inventory-turnover")}
        onToggle={() => {
          handleAccordionToggle("inventory-turnover")
          eventTag(eventLabGA?.name, eventLabGA?.category, eventLabGA?.action, `Accordion: Giro de Estoque`)
        }}
      >
        <PostCreditSection
          step={7}
          data={inventoryTurnoverData}
          onChange={handleArrayChange(setInventoryTurnoverData)}
          isDisabled={isUpdating}
          onInvalidChange={handleInvalidFormChange('inventory')}
          lab
        />
      </AccordionGraphics>

      <AccordionGraphics
        title="Capacidade de Pagamento"
        isOpen={openedSections.includes("payment-capacity")}
        onToggle={() => {
          handleAccordionToggle("payment-capacity")
          eventTag(eventLabGA?.name, eventLabGA?.category, eventLabGA?.action, `Accordion: Capacidade de Pagamento`)
        }}
      >
        <PostCreditCashFlow
          date={moment(clickedMonth).format("MMMM YYYY")}
          data={paymentCapacityData}
          onChange={handleArrayChange(setPaymentCapacityData)}
          income={planningProfit}
          lab
        />
      </AccordionGraphics>

      <div className="lab-modal-edit-footer">
        <div className="lab-modal-edit-footer-content">
          <p>{renderError()}</p>
          <Button
            callback={handleUpdateAll}
            disabled={isUpdating || Object.values(invalidForm).some(isInvalid => isInvalid)}
            isPrimary={true}
            maxWidth={119}
          >
            {isUpdating ? "Atualizando..." : "Atualizar"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditLabPlanning;
